import { Heading, Input, LuIcon, SegmentedTabs, Text } from "@overmap-ai/blocks"
import { type ProcedureType, type Stored } from "@overmap-ai/common"
import { selectProceduresOfProject, selectProcedureTypesOfOrganization } from "@overmap-ai/core"
import { useTextFilter } from "@overmap-ai/hooks"
import { getRouteApi, useNavigate } from "@tanstack/react-router"
import { memo, useCallback, useMemo } from "react"

import { ViewContainer } from "../../components/Containers/ViewContainer"
import { useProcedureTypeTable, useProcedureTypeTableColumns } from "../../components/Tables"
import { Table } from "../../components/Tables/Table"
import { procedureTypeFilterCallback } from "../../filtering/utils"
import { useChangeEventHandler } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useShallowEqaulSelector } from "../../store"

enum ProcedureTypesViewTabs {
	PROJECT = "project",
	OTHER = "other",
}

const routeApi = getRouteApi("/_loggedIn/$organizationId/_map/_layout/$projectId/_projectMap/procedure-types/")

export const ProcedureTypesView = memo(() => {
	const { organizationId, projectId } = routeApi.useParams()

	const procedureTypes = useShallowEqaulSelector(selectProcedureTypesOfOrganization(organizationId))
	const procedures = useShallowEqaulSelector(selectProceduresOfProject(projectId))
	const navigate = useNavigate()

	const handleView = useCallback(
		(procedureType: Stored<ProcedureType>) => {
			void navigate({
				from: "/$organizationId/$projectId/procedure-types/",
				to: "/$organizationId/$projectId/procedure-types/$procedureTypeId",
				params: (prev) => ({ ...prev, procedureTypeId: procedureType.uuid }),
			})
		},
		[navigate],
	)

	const [projectProcedureTypes, otherProcedureTypes] = useMemo(() => {
		const projectProcedureTypeIds = new Set(procedures.map((procedure) => procedure.procedure_type))

		const projectTypes: Stored<ProcedureType>[] = []
		const otherTypes: Stored<ProcedureType>[] = []

		for (const procedureType of procedureTypes) {
			if (projectProcedureTypeIds.has(procedureType.uuid)) {
				projectTypes.push(procedureType)
			} else {
				otherTypes.push(procedureType)
			}
		}

		return [projectTypes, otherTypes]
	}, [procedures, procedureTypes])

	const [filteredProjectProcedureTypes, projectProcedureTypeFilterValue, setProjectProcedureTypeFilterValue] =
		useTextFilter(projectProcedureTypes, procedureTypeFilterCallback)

	const [filteredOtherProcedureTypes, otherProcedureTypeFilterValue, setOtherProcedureTypeFilterValue] =
		useTextFilter(otherProcedureTypes, procedureTypeFilterCallback)

	const columns = useProcedureTypeTableColumns({
		procedureTypes,
		procedures: procedures,
		onView: handleView,
	})

	const projectTable = useProcedureTypeTable({
		data: filteredProjectProcedureTypes,
		columns,
		enableHiding: false,
	})

	const otherTable = useProcedureTypeTable({
		data: filteredOtherProcedureTypes,
		columns,
		enableHiding: false,
	})

	return (
		<ViewContainer maxWidth="6xl">
			<div className="flex size-full flex-col gap-4 overflow-hidden">
				<div className="flex flex-col gap-1">
					<div className="flex justify-between gap-2">
						<Heading size="xl">Procedures</Heading>
					</div>

					<Text size="sm" accentColor="base">
						Procedure types let you review the procedures currently applied across issues and assets in this
						project.
					</Text>
				</div>

				<SegmentedTabs.Root
					defaultValue={ProcedureTypesViewTabs.PROJECT}
					className="flex size-full flex-col gap-4 overflow-hidden"
				>
					<SegmentedTabs.List size="sm" accentColor="base" variant="surface">
						<SegmentedTabs.Trigger value={ProcedureTypesViewTabs.PROJECT}>
							Project {projectProcedureTypes.length > 0 && `(${projectProcedureTypes.length})`}
						</SegmentedTabs.Trigger>
						<SegmentedTabs.Trigger value={ProcedureTypesViewTabs.OTHER}>
							Other {otherProcedureTypes.length > 0 && `(${otherProcedureTypes.length})`}
						</SegmentedTabs.Trigger>
					</SegmentedTabs.List>

					<SegmentedTabs.Content
						value={ProcedureTypesViewTabs.PROJECT}
						className="flex size-full flex-col gap-2 overflow-hidden"
					>
						<div className="flex w-full items-center justify-between gap-2">
							<Input.Root variant="outline">
								<Input.Slot>
									<LuIcon icon={modelIconMapping.search} />
								</Input.Slot>
								<Input.Field
									placeholder="Search"
									value={projectProcedureTypeFilterValue}
									onChange={useChangeEventHandler(setProjectProcedureTypeFilterValue)}
								/>
							</Input.Root>
						</div>

						<div className="size-full overflow-auto">
							<Table
								className="w-full"
								size="sm"
								variant="surface"
								table={{ ...projectTable }}
								onRowClick={handleView}
								interactive
								emptyText="No procedure types."
							/>
						</div>
					</SegmentedTabs.Content>

					<SegmentedTabs.Content
						value={ProcedureTypesViewTabs.OTHER}
						className="flex size-full flex-col gap-2 overflow-hidden"
					>
						<div className="flex w-full items-center justify-between gap-2">
							<Input.Root variant="outline">
								<Input.Slot>
									<LuIcon icon={modelIconMapping.search} />
								</Input.Slot>
								<Input.Field
									placeholder="Search"
									value={otherProcedureTypeFilterValue}
									onChange={useChangeEventHandler(setOtherProcedureTypeFilterValue)}
								/>
							</Input.Root>
						</div>

						<div className="size-full overflow-auto">
							<Table
								className="w-full"
								size="sm"
								variant="surface"
								table={{ ...otherTable }}
								onRowClick={handleView}
								interactive
								emptyText="No procedure types."
							/>
						</div>
					</SegmentedTabs.Content>
				</SegmentedTabs.Root>
			</div>
		</ViewContainer>
	)
})
ProcedureTypesView.displayName = "ProcedureTypesView"
